/**
 *
 * Sidebar
 *
 */

import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ClickAwayListener } from '@mui/material';
import { format, parseISO } from 'date-fns';
import Picker, { IEmojiData } from 'emoji-picker-react';
import React, { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useMobileMediaQuery from '../../../../../hooks/useMobileMediaQuery';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { selectCards, selectEvents } from '../../../../../redux/trip/trip.selectors';
import { removeList, updateActiveListId, updateList } from '../../../../../redux/trip/trip.slice';
import { TripList } from '../../../../../redux/trip/trip.types';
import { ItemType } from '../../../../../types/DragAndDrop';
import { SORTABLE_TRANSITION } from '../../../../Trip/DnD/types';
import { useSidebarUI } from '../../SidebarUIContext';
import ItemOptionsMenu from '../ItemOptionsMenu/ItemOptionsMenu';
import RenameListInput from '../RenameListInput/RenameListInput';
import './ListItem.scss';

type ListItemProps = {
  tripId: string;
  list: TripList;
  listOrder: string[];
  isActive?: boolean;
  canEdit?: boolean;
  isEditingName: boolean;
  onStartEditing: () => void;
  onFinishEditing: () => void;
  cardCount?: number;
  dividerId?: string;
};

function ListItem({
  tripId,
  list,
  listOrder,
  isActive = false,
  canEdit = false,
  isEditingName,
  onStartEditing,
  onFinishEditing,
  cardCount,
  dividerId,
}: ListItemProps) {
  const [_searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMobileMediaQuery();
  const dispatch = useAppDispatch();
  const events = useAppSelector(selectEvents);
  const cards = useAppSelector(selectCards);
  const { openEmojiId, setOpenEmojiId, openMenuId, setOpenMenuId } = useSidebarUI();
  const isEditingEmoji = openEmojiId === list.id;
  const anchorElRef = useRef<HTMLDivElement | null>(null);
  const anchorEl = openMenuId === list.id ? anchorElRef.current : null;
  const index = useMemo(() => listOrder.indexOf(list.id), [listOrder, list.id]);

  // Sortable: makes this list draggable and reorderable within its divider
  const {
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
    listeners,
    attributes,
  } = useSortable({
    id: list.id,
    data: {
      type: ItemType.LIST,
      listId: list.id,
      index,
      dividerId,
      dividerListOrder: dividerId ? listOrder : undefined,
    },
    disabled: !canEdit || isEditingName,
    transition: SORTABLE_TRANSITION,
  });

  // Droppable: accepts cards dragged onto this list item (cross-list move).
  // Uses a separate id so it can be disabled when this list is active, while
  // the sortable droppable (id=list.id) remains active for list reordering.
  const { setNodeRef: setCardDropRef } = useDroppable({
    id: `card-drop-${list.id}`,
    data: { type: ItemType.LIST, listId: list.id, dividerId },
    disabled: isActive,
  });

  // Track when a card is being dragged over this list for the highlight indicator.
  // useDroppable's isOver is unreliable here because the sortable and droppable
  // share the same DOM node — the sortable id wins in collision detection.
  const [isCardDragOver, setIsCardDragOver] = useState(false);
  useDndMonitor({
    onDragMove(event) {
      const isCard = event.active.data.current?.type === ItemType.CARD;
      const overId = event.over?.id as string | undefined;
      setIsCardDragOver(
        isCard && (overId === list.id || overId === `card-drop-${list.id}`) && !isActive
      );
    },
    onDragEnd() {
      setIsCardDragOver(false);
    },
    onDragCancel() {
      setIsCardDragOver(false);
    },
  });

  // Merge the sortable ref + card-droppable ref onto the same DOM node
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      setSortableRef(node);
      setCardDropRef(node);
    },
    [setSortableRef, setCardDropRef]
  );

  // Cards in this list that are scheduled in the itinerary, with formatted day labels
  const scheduledCards = useMemo(() => {
    const cardIds = list.cardOrder ?? [];
    return cardIds
      .map((cardId) => {
        const dayLabels = Object.values(events ?? {})
          .filter((e) => e.cardId === cardId)
          .map((e) => format(parseISO(e.startDateISO), 'EEEE, MMMM d'))
          .sort();
        const cardName = cards?.[cardId]?.customName ?? cards?.[cardId]?.name ?? cardId;
        return dayLabels.length > 0 ? { cardId, cardName, dayLabels } : null;
      })
      .filter(Boolean) as Array<{ cardId: string; cardName: string; dayLabels: string[] }>;
  }, [events, cards, list.cardOrder]);

  const itineraryWarning =
    scheduledCards.length > 0 ? (
      <span style={{ display: 'block', marginTop: 8 }}>
        The following cards are also scheduled in your itinerary and will be removed:
        <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
          {scheduledCards.map(({ cardId, cardName, dayLabels }) => (
            <li key={cardId}>
              <strong>{cardName}</strong>
              {dayLabels.map((day) => (
                <div
                  key={day}
                  style={{ paddingLeft: 8 }}
                >
                  - {day}
                </div>
              ))}
            </li>
          ))}
        </ul>
      </span>
    ) : null;

  const handleOptionsOpen = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      anchorElRef.current = event.currentTarget;
      setOpenMenuId(list.id);
    },
    [list.id, setOpenMenuId]
  );

  // triggers the rename input for this list
  const editListName = useCallback(() => onStartEditing(), [onStartEditing]);

  // opens the emoji picker for this list (only available to editors on desktop)
  const handleListEmojiEdit = useMemo(
    () =>
      canEdit
        ? (event: React.MouseEvent<HTMLLIElement>) => {
            event.stopPropagation();
            setOpenMenuId(null);
            setTimeout(() => setOpenEmojiId(list.id), 0);
          }
        : undefined,
    [canEdit, list.id, setOpenEmojiId, setOpenMenuId]
  );

  // saves the emoji the user picked and closes the picker
  const onEmojiClick = useCallback(
    (event: MouseEvent, emojiObject: IEmojiData) => {
      event.stopPropagation();
      setOpenEmojiId(null);
      dispatch(
        updateList({
          tripId,
          listId: list.id,
          field: 'emoji',
          value: emojiObject.emoji,
        })
      );
    },
    [dispatch, tripId, list.id, setOpenEmojiId]
  );

  // sets this list as active and updates the URL so the view reflects the selection
  const handleListClick = useCallback(() => {
    dispatch(updateActiveListId(list.id));
    setSearchParams((prev) => {
      prev.set('list', list.id);
      return prev;
    });
  }, [dispatch, list.id, setSearchParams]);

  // builds the CSS class string based on current state
  const containerClass = useMemo(
    () =>
      [
        'list-item-container',
        isActive && ' selected',
        isDragging && ' dragging',
        isCardDragOver && ' is-over',
        isMobile && ' mobile',
      ]
        .filter(Boolean)
        .join(' '),
    [isActive, isDragging, isCardDragOver, isMobile]
  );

  const handleClickAway = useCallback(() => setOpenEmojiId(null), [setOpenEmojiId]);

  return (
    <>
      <div
        ref={setRef}
        className={containerClass}
        style={{ transform: CSS.Translate.toString(transform), transition }}
        {...listeners}
        {...attributes}
      >
        <div className='list-item-div'>
          {isEditingName ? (
            <RenameListInput
              tripId={tripId}
              list={list}
              canEdit={canEdit}
              isEditingName={isEditingName}
              setIsEditingName={onFinishEditing}
              isMobile={isMobile}
            />
          ) : (
            <div className='list-item-info'>
              <span
                className='list-item-emoji'
                onClick={
                  isMobile
                    ? undefined
                    : (handleListEmojiEdit as React.MouseEventHandler<HTMLSpanElement> | undefined)
                }
              >
                {list.emoji}
              </span>
              <span
                className={`list-item-name${isActive ? ' selected' : ''}`}
                onClick={handleListClick}
                title={list.name}
              >
                {list.name}
              </span>
              {isMobile && isActive && cardCount !== undefined && cardCount > 0 && (
                <span className='list-item-card-count'>
                  {cardCount === 1 ? '1 place' : `${cardCount} places`}
                </span>
              )}
            </div>
          )}
          {isEditingEmoji && (
            <ClickAwayListener onClickAway={handleClickAway}>
              <div
                className={`emoji-picker-div${isMobile ? ' mobile' : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Picker onEmojiClick={onEmojiClick} />
              </div>
            </ClickAwayListener>
          )}
        </div>

        {canEdit && isActive && !isEditingName && (
          <div
            className={`list-options-div${isMobile ? ' mobile' : ''}`}
            onClick={handleOptionsOpen}
          >
            •••
          </div>
        )}
      </div>

      <ItemOptionsMenu
        itemName={list.name}
        isMobile={isMobile}
        anchorEl={anchorEl}
        setAnchorEl={() => setOpenMenuId(null)}
        onRename={editListName}
        onEmojiEdit={handleListEmojiEdit}
        onDelete={() =>
          dispatch(
            removeList({ tripId, listId: list.id, listOrder, cardIds: list.cardOrder ?? [] })
          )
        }
        deleteMessage={
          <>
            Deleting {list.emoji ? <>{list.emoji} </> : null}
            <strong>{list.name}</strong> will remove all cards in the list.
            {itineraryWarning}
          </>
        }
      />
    </>
  );
}

ListItem.displayName = 'ListItem';
export default ListItem;
